import Controller from '@ember/controller';
import { service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type RouterService from '@ember/routing/router-service';
import type Owner from '@ember/owner';
import type { Store } from '@warp-drive/core';
import type Modals from '../services/modals';
import type LocationService from '../services/location';
import type NetworkStatus from '../services/network-status';
import type { ItemStats } from '../schemas/item-stats.ts';
import { query } from '@warp-drive/utilities/json-api';
import { haversineDistance, formatDistance } from '../utils/distance';
import { hasCoordinates } from '../utils/map';
import type { PlaceMarker } from '../routes/places';

export default class PlacesController extends Controller {
  @service declare store: Store;
  @service declare router: RouterService;
  @service declare modals: Modals;
  @service declare location: LocationService;
  @service declare networkStatus: NetworkStatus;

  @tracked items: ItemStats[] = [];
  @tracked markers: PlaceMarker[] = [];
  @tracked mappedCount = 0;
  @tracked loadError: Error | null = null;
  @tracked activeTab: 'map' | 'list' = 'map';

  get isMapTab(): boolean {
    return this.activeTab === 'map';
  }
  get isListTab(): boolean {
    return this.activeTab === 'list';
  }
  @tracked hasPromptedLocation = false;

  private isLoading = false;

  constructor(owner: Owner) {
    super(owner);
    this.networkStatus.onReconnect(() => {
      if (this.router.currentRouteName === 'places') {
        void this.loadData();
      }
    });
  }

  get userLocation(): { latitude: number; longitude: number } | null {
    return this.location.userLocation;
  }

  @action
  async loadData() {
    if (this.isLoading) return;
    this.isLoading = true;
    this.loadError = null;
    try {
      // Fresh fetch so pull-to-refresh bypasses the WarpDrive request cache
      const { content } = await this.store.request({
        ...query(
          'item-stat',
          {
            'filter[type]': 'place',
            'page[number]': '1',
            'page[size]': '200',
            sort: '-createdAt',
          },
          { resourcePath: 'items/stats' }
        ),
        cacheOptions: { reload: true },
      });
      const json = content as unknown as { data?: ItemStats[] };
      const items = json.data ?? [];
      const markers = items
        .map((item) => getMarker(item))
        .filter((marker): marker is PlaceMarker => marker !== null);
      this.items = items;
      this.markers = markers;
      this.mappedCount = markers.length;
    } catch (error) {
      this.loadError =
        error instanceof Error ? error : new Error('Failed to load places');
    } finally {
      this.isLoading = false;
    }
  }

  @action
  async refresh() {
    await this.loadData();
  }

  @action
  setTab(tab: 'map' | 'list') {
    this.activeTab = tab;
    if (tab === 'map' && !this.hasPromptedLocation) {
      void this.requestLocation();
    }
  }

  @action
  async requestLocation() {
    this.hasPromptedLocation = true;
    const coords = await this.location.requestWithPrompt(this.modals);
    if (coords) {
      this.updateDistances(coords);
    }
  }

  private updateDistances(userLocation: {
    latitude: number;
    longitude: number;
  }) {
    this.markers = this.markers.map((marker) => {
      const km = haversineDistance(userLocation, {
        latitude: marker.latitude,
        longitude: marker.longitude,
      });
      return {
        ...marker,
        distance: formatDistance(km),
      };
    });
  }
}

function getMarker(item: ItemStats): PlaceMarker | null {
  let data: unknown = item.enrichedData;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data) as unknown;
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== 'object') return null;

  const rawLocation = (data as { location?: unknown }).location;
  if (!hasCoordinates(rawLocation)) return null;

  return {
    id: String(item.id),
    name: asString(item.name),
    latitude: rawLocation.latitude,
    longitude: rawLocation.longitude,
    ...(item.emoji ? { emoji: item.emoji } : {}),
  };
}

function asString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
}
